import { Fragment, useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">Tips to make healthy habits stick</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">Get your 5-a-day</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">Ditch the excuses</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">4.</div>
        <div className="navigator__contents__content-text">
          <a href="#4">Understand portion sizes</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">5.</div>
        <div className="navigator__contents__content-text">
          <a href="#5">Always read the label</a>
        </div>
      </li>{' '}
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">{'Week 11: Making changes stick'}</h1>
      <p>
        {`This week we're sharing swaps and changes you can make to your eating habits, from getting portions right to
        getting your five-a-day. We'll also help you make your new habits work in the long term so that you can keep
        your cholesterol at a healthy level.`}
      </p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>Tips to make healthy habits stick</h2>
      <p>
        {`Starting a new, healthier lifestyle can be exciting. But after a while you may find old habits start creeping in
        and undoing your hard work. We're sharing our top 3 tips to make sure you stick to your goals and lower your
        cholesterol.`}
      </p>
      <NumberedList
        items={[
          <Fragment key="1">
            <strong>{`Focus on what you want to eat rather than what you don't.`}</strong> For example, making sure you have vegetables at every meal feels more positive than obsessing about what to avoid.
          </Fragment>,
          <Fragment key="2">
            <strong>Break it down.</strong>
            Sometimes long-term goals can be intimidating, so try breaking each one down. What choices can you make today to improve your cholesterol levels?
          </Fragment>,
          <Fragment key="3">
            <strong>Rehearse barriers.</strong>{` Life is unpredictable and things will knock you off-track. It's important to see these coming and think about how you're going to deal with them ahead of time. Look at online menus before going to restaurants and practice saying no thanks to unwanted calories. Remember, every time you make a good choice, you're building a habit and making it easier next time.`}
          </Fragment>,
        ]}
      />
      <section className="navigator__section-half">
        <p>
          {`Remember, it's the small choices, made in the moment, that build up to the changes you want to see. Don't put
          off starting until tomorrow. Start where you are, and make one good choice after another. If you lose
          motivation, don't be hard on yourself, just focus on making the next choice a healthy choice.`}
        </p>
      </section>
      <img alt="Cutting a piece of paper that says I can't" className="navigator__image-half" src={`${PROGRAM_URL}W11B01.jpg`} />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/how-to-set-goals"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Get more tips here"
        />
      </a>

      <h2>Get your 5-a-day</h2>
      <p>
        {`Most people would benefit from eating more vegetables and fruit. They are an important part of a balanced diet
        and a key part of losing weight. If you're struggling to fit in your 5 portions of fruit and veg a day, here are
        some simple tips to get you started.`}{' '}
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/Uj8_Na-3YBA"
        />
      </div>

      <h2>Ditch the excuses</h2>
      <NumberedList
        items={[
          <Fragment key="1">
            <strong>{`I'm too tired.`}</strong> Remind yourself that being active actually gives you more energy. Once you
            get in the habit of being active regularly, your energy levels will go up overall.
          </Fragment>,
          <Fragment key="2">
            <strong>{`I'm not overweight, so I don't need to be active.`}</strong>{` Anyone can have high cholesterol, even if
            they're not overweight. Exercise is important for your overall health. It can make you stronger, fitter and
            help you cope with stress. It will also help you get your cholesterol to a healthy level, regardless of your
            weight.`}
          </Fragment>,
          <Fragment key="3">
            <strong>Getting active is too expensive.</strong>{` Even something as simple as a walk or jog counts towards
            your weekly amount of exercise. If you don't want to go out try doing some simples exercises at home. Use
            the stairs for step-ups or tins of beans as small weights.`}
          </Fragment>,
        ]}
      />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/ditch-the-excuses"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="More motivational tips"
        />
      </a>

      <h2>Understand portion sizes</h2>
      <p>
        Eating the right amount of food is just as important as the types of food you eat. Women who are trying to stay
        the same weight should aim to eat around 2,000 calories a day, and men trying to stay the same weight should aim
        for 2,500 calories, depending on your age, size and how active you are. These calories should come from lots of
        different, heart-healthy foods throughout the day.
      </p>
      <p>
        Generally people eat a lot more than they need to feel full, and one of the easiest ways to achieve a healthy
        weight is to get your portion sizes right.
        Using smaller plates and glasses will help you, but have a look at the portion sizes in the guide below and start using them.
      </p>
      <p>
        With packaged food, always read the label to find out how much one portion is. Make sure most of your plate is
        made up of vegetables and fruit, then a portion of starchy carbohydrates, with a smaller amount of protein and
        just a little bit of healthy fats.
      </p>
      <a
        href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/portion-guide"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Get the portions guide"
        />
      </a>

      <h2>Always read the label</h2>
      <section className="navigator__section-half">
        <p>
          Reading food labels is an important habit to get into if you want to make good decisions every time you are
          grocery shopping. They can tell you how many calories are in that food, as well as the amount of fat,
          saturated fat, salt and sugar.{' '}
        </p>
        <p>
          Try to choose mainly green labels with a few ambers, and avoid reds as much as you can. The label will also
          tell you the portion size so you can be careful not to eat too much.
        </p>
      </section>
      <img alt="People shopping in a super market" className="navigator__image-half" src={`${PROGRAM_URL}W11B05.jpg`} />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/sugar-salt-and-fat/10-tips-for-understanding-food-labels"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Get your full label reading guide"
        />
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Join the conversation on HealthUnlocked</div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on
          HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice
          and encouragement.`}{' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
